import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "../assets/images/Bildup-Icon.svg";
import { Icon } from "@iconify/react";
import { motion, AnimatePresence } from "framer-motion";
import StartTrial from "./StartTrial";

const Navigation = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "Home", path: "/" },
    { label: "Early Adapters", path: "/early-adopters" },
  ];

  const renderNavItem = (item, index) => {
    const isActive = location.pathname === item.path;

    return (
      <li key={index}>
        <Link
          to={item.path}
          className={`flex text-xl items-center ${
            scrolled ? "opacity-75" : "opacity-60"
          } group py-2 px-3 rounded md:p-0 transition-all ease-in-out duration-500 ${
            isActive
              ? "text-primary-purple-100"
              : scrolled
              ? "text-gray-50 hover:text-primary-purple-200"
              : "text-gray-900 hover:text-primary-purple-200"
          }`}
        >
          <span>{item.label}</span>
          <div className="ml-1 relative">
            <Icon
              icon="solar:arrow-right-broken"
              className={`w-5 h-5 transition-all ease-in-out duration-500 ${
                isActive
                  ? "text-primary-purple-200 -rotate-45"
                  : scrolled
                  ? "md:text-gray-50 text-gray-400 group-hover:-rotate-45 group-hover:text-primary-purple-200 group-hover:translate-x-1"
                  : "text-black group-hover:-rotate-45 group-hover:text-primary-purple-200 group-hover:translate-x-1"
              }`}
            />
          </div>
        </Link>
      </li>
    );
  };

  return (
    <motion.nav
      initial={false}
      animate={scrolled ? "scrolled" : "top"}
     
      transition={{ duration: 0.3 }}
      className={`fixed top-0 left-0 right-0 z-[800] transition-all duration-500 ${
        scrolled 
          ? 'gradient-primary backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-xl flex flex-wrap items-center justify-between mx-auto p-4">
        <Link to="/" className="flex items-center space-x-3">
          <span className="self-center text-2xl w-[40px] h-[40px] font-semibold whitespace-nowrap text-primary-700">
            <img src={Logo} alt="logo" className="w-full h-full" />
          </span>
        </Link>

        <button
          type="button"
          className="inline-flex items-center p-2 w-10 h-10 justify-center text-gray-500 rounded-lg md:hidden hover:bg-gray-100"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <span className="sr-only">Toggle menu</span>
          <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 17 14"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M1 1h15M1 7h15M1 13h15"
            />
          </svg>
        </button>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              key="mobileMenu"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className={`w-full md:hidden mt-4 rounded-lg px-4 py-2 overflow-hidden gradient-primary backdrop-blur-md `}
            >
              <ul className="flex flex-col gap-2">
                {navItems.map((item, index) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={index}>
                      <Link
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center justify-between text-lg py-2 px-3 rounded transition-all duration-300 ${
                          isActive
                            ? "text-primary-purple-200"
                            : scrolled
                            ? "text-white hover:text-primary-purple-200"
                            : "text-black hover:text-primary-purple-600"
                        }`}
                      >
                        {item.label}
                        <Icon
                          icon="solar:arrow-right-broken"
                          className="w-5 h-5 ml-2"
                        />
                      </Link>
                    </li>
                  );
                })}
                <li className="mt-2">
                  <StartTrial />
                </li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Desktop Nav */}
        <div className="hidden md:block md:w-auto">
          <ul className="font-medium flex flex-row items-center space-x-8">
            {navItems.map(renderNavItem)}
            <li className="md:ml-4">
              <StartTrial />
            </li>
          </ul>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navigation;
